import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Save, Image } from 'lucide-react';
import { projectsApi, uploadApi } from '@/services/api';
import { toast } from 'sonner';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title_en: '',
    title_ar: '',
    description_en: '',
    description_ar: '',
    category: 'wood',
    images: [],
    before_image: '',
    after_image: '',
    featured: false,
    order: 0
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await projectsApi.getAll();
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingProject) {
        await projectsApi.update(editingProject.id, formData);
        toast.success('Project updated');
      } else {
        await projectsApi.create(formData);
        toast.success('Project created');
      }
      await fetchProjects();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await projectsApi.remove(id);
      toast.success('Project deleted');
      setProjects((prev) => prev.filter((p) => p.id !== id));
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const openEditModal = (project) => {
    setEditingProject(project);
    setFormData({
      title_en: project.title_en,
      title_ar: project.title_ar,
      description_en: project.description_en,
      description_ar: project.description_ar,
      category: project.category,
      images: project.images || [],
      before_image: project.before_image || '',
      after_image: project.after_image || '',
      featured: project.featured,
      order: project.order
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProject(null);
    setFormData({
      title_en: '',
      title_ar: '',
      description_en: '',
      description_ar: '',
      category: 'wood',
      images: [],
      before_image: '',
      after_image: '',
      featured: false,
      order: 0
    });
  };

  const addImageUrl = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      setFormData({ ...formData, images: [...formData.images, url] });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadApi.image(file);
      setFormData((prev) => ({ ...prev, images: [...prev.images, res.data.url] }));
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    }
    e.target.value = '';
  };

  const removeImage = (index) => {
    setFormData({ ...formData, images: formData.images.filter((_, i) => i !== index) });
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Projects</h1>
          <p className="text-white/60">Manage your kitchen projects</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="add-project-btn"
          className="flex items-center gap-2 bg-gold text-charcoal px-4 py-2 font-medium hover:bg-gold/90 transition-colors"
        >
          <Plus size={20} />
          Add Project
        </button>
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="aspect-[4/3] bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white/5 border border-white/10 overflow-hidden group"
            >
              <div className="aspect-[4/3] relative">
                <img
                  src={project.images?.[0] || 'https://images.pexels.com/photos/29925933/pexels-photo-29925933.jpeg'}
                  alt={project.title_en}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
                {project.featured && (
                  <span className="absolute top-2 start-2 bg-gold text-charcoal text-xs px-2 py-1">
                    Featured
                  </span>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                  <button
                    onClick={() => openEditModal(project)}
                    data-testid={`edit-project-${project.id}`}
                    className="w-10 h-10 bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-gold hover:text-charcoal transition-colors"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(project.id)}
                    data-testid={`delete-project-${project.id}`}
                    className="w-10 h-10 bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-red-500 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <span className="text-gold text-xs uppercase tracking-wider">{project.category}</span>
                <h3 className="text-white font-medium mt-1">{project.title_en}</h3>
                <p className="text-white/40 text-sm">{project.title_ar}</p>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-brand-panel w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-xl font-bold text-white">
                {editingProject ? 'Edit Project' : 'Add New Project'}
              </h2>
              <button onClick={closeModal} className="text-white/60 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white/60 text-sm mb-2">Title (English)</label>
                  <input
                    type="text"
                    value={formData.title_en}
                    onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                    required
                    data-testid="project-title-en"
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-2">Title (Arabic)</label>
                  <input
                    type="text"
                    value={formData.title_ar}
                    onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })}
                    required
                    data-testid="project-title-ar"
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white/60 text-sm mb-2">Description (English)</label>
                  <textarea
                    value={formData.description_en}
                    onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                    required
                    rows={3}
                    data-testid="project-desc-en"
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none resize-none"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-2">Description (Arabic)</label>
                  <textarea
                    value={formData.description_ar}
                    onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })}
                    required
                    rows={3}
                    data-testid="project-desc-ar"
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none resize-none"
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white/60 text-sm mb-2">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    data-testid="project-category"
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  >
                    <option value="wood">Wood Kitchens</option>
                    <option value="aluminum">Wood & Aluminum</option>
                    <option value="modern">Modern</option>
                  </select>
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-2">Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                    data-testid="project-order"
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  />
                </div>
              </div>

              {/* Images */}
              <div>
                <label className="block text-white/60 text-sm mb-2">Images</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.images.map((img, index) => (
                    <div key={index} className="relative w-20 h-20">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -end-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={addImageUrl}
                    className="flex items-center gap-2 text-gold text-sm hover:underline"
                  >
                    <Image size={16} />
                    Add Image URL
                  </button>
                  <label className="flex items-center gap-2 text-white/60 text-sm cursor-pointer hover:text-gold transition-colors">
                    <Image size={16} />
                    Upload File
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                  </label>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white/60 text-sm mb-2">Before Image URL</label>
                  <input
                    type="text"
                    value={formData.before_image}
                    onChange={(e) => setFormData({ ...formData, before_image: e.target.value })}
                    data-testid="project-before"
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-2">After Image URL</label>
                  <input
                    type="text"
                    value={formData.after_image}
                    onChange={(e) => setFormData({ ...formData, after_image: e.target.value })}
                    data-testid="project-after"
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  data-testid="project-featured"
                  className="w-4 h-4"
                />
                <label htmlFor="featured" className="text-white">Featured Project</label>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 border border-white/20 text-white py-3 hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="save-project-btn"
                  className="flex-1 bg-gold text-charcoal py-3 font-medium hover:bg-gold/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Save size={18} />
                  {saving ? 'Saving…' : 'Save Project'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminProjects;
